import { FC, useEffect, useState, useRef } from "react";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@capacit-com/lara-law-shared-ui";
import { cn } from "@/lib/utils";

interface TruncatedTextProps {
  text: string;
  /** Class name for the truncated text container */
  className?: string;
  /** Class name for the tooltip content wrapper */
  tooltipClassName?: string;
  /** Class name for the text inside the tooltip (the <p> tag) */
  tooltipTextClassName?: string;
  /** Delay before showing tooltip in ms */
  delayDuration?: number;
  /** Side where tooltip appears */
  side?: "top" | "right" | "bottom" | "left";
  /** Offset from the trigger element */
  sideOffset?: number;
}

/**
 * Component that displays truncated text with an ellipsis and shows a tooltip
 * ONLY when the text is actually truncated. Useful for table cells, cards, etc.
 */
const TruncatedText: FC<TruncatedTextProps> = ({
  text,
  className,
  tooltipClassName,
  tooltipTextClassName,
  delayDuration = 300,
  side = "bottom",
  sideOffset = 5,
}) => {
  const textRef = useRef<HTMLDivElement>(null);
  const [isTruncated, setIsTruncated] = useState(false);

  useEffect(() => {
    const checkTruncation = () => {
      if (textRef.current) {
        setIsTruncated(
          textRef.current.scrollWidth > textRef.current.clientWidth
        );
      }
    };

    checkTruncation();
    // Recheck on window resize
    window.addEventListener("resize", checkTruncation);
    return () => window.removeEventListener("resize", checkTruncation);
  }, [text]);

  const content = (
    <div ref={textRef} className={cn("truncate block", className)}>
      {text}
    </div>
  );

  // Don't show tooltip if text is not truncated
  if (!isTruncated) {
    return content;
  }

  return (
    <TooltipProvider delayDuration={delayDuration}>
      <Tooltip>
        <TooltipTrigger asChild>{content}</TooltipTrigger>
        <TooltipContent
          side={side}
          className={cn("max-w-md pointer-events-none", tooltipClassName)}
          sideOffset={sideOffset}
        >
          <p className={cn("text-sm", tooltipTextClassName)}>{text}</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

export default TruncatedText;
